import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, Select, useAdminGet } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Badge } from '../emailKit';
import { SECTION, Trigger, fmtIST, num } from './shared';
import { reasonLabel } from './DryRun';

/**
 * MEMAC → Runs & log (email/memac_runs): every evaluation — dry run, manual
 * send, automatic run — with what it found (detected / eligible / left out /
 * capped / sent / failed), and for one trigger the ledger rows it wrote
 * (email_template_sends with trigger_id: who, when, which occurrence, status).
 */

const KIND_TONE: Record<string, 'blue' | 'amber' | 'red' | 'green' | 'grey'> = {
  dry_run: 'grey', manual: 'blue', auto: 'amber', test: 'grey',
};
const STATUS_TONE: Record<string, 'blue' | 'amber' | 'red' | 'green' | 'grey'> = {
  sent: 'green', queued: 'amber', failed: 'red', skipped: 'grey',
};

/** {reason: n} as "reason n · reason n". */
const reasons = (o: any): string => {
  if (!o || typeof o !== 'object') return '';
  return Object.entries(o).filter(([, v]) => Number(v) > 0)
    .map(([k, v]) => `${reasonLabel(k)} ${num(v)}`).join(' · ');
};

const Runs: React.FC<{ triggers: Trigger[]; initial?: string; onOpenTrigger: (id: string) => void }> =
  ({ triggers, initial, onOpenTrigger }) => {
  const [tid, setTid] = useState(initial || '');
  const [page, setPage] = useState(0);
  useEffect(() => { if (initial !== undefined) setTid(initial); }, [initial]);
  useEffect(() => { setPage(0); }, [tid]);
  const params: Record<string, string | number> = { page, ...(tid ? { trigger_id: tid } : {}) };
  const res = useAdminGet<any>(SECTION, 'memac_runs', params, [tid, page]);
  const d = res.data || {};
  const runs: any[] = Array.isArray(d.runs) ? d.runs : Array.isArray(d.rows) ? d.rows : Array.isArray(d) ? d : [];
  const ledger: any[] = Array.isArray(d.ledger) ? d.ledger : Array.isArray(d.sends) ? d.sends : [];
  // memac.runs: {runs, runs_total, per_page, sends, sends_total, sends_per_page}
  const total = d.runs_total !== undefined ? Number(d.runs_total) : d.total !== undefined ? Number(d.total) : null;
  const perPage = Number(d.per_page) || 0;
  const sendsTotal = d.sends_total !== undefined ? Number(d.sends_total) : null;
  const sendsPer = Number(d.sends_per_page) || 0;
  const pages = Math.max(1, Number(d.pages) || (total !== null && perPage ? Math.ceil(total / perPage) : 1),
    sendsTotal !== null && sendsPer ? Math.ceil(sendsTotal / sendsPer) : 1);

  const runRows = useMemo(() => runs.map(r => ({
    ...r,
    started_at: r.started_at || r.at || r.created_at,
    excluded_text: reasons(r.excluded),
    capped_text: reasons(r.capped),
  })), [runs]);

  const options = ['', ...triggers.filter(t => !t.external).map(t => t.id)];
  const labels: Record<string, string> = {
    '': 'All triggers', ...Object.fromEntries(triggers.map(t => [t.id, `${t.id} · ${t.name}`])),
  };
  if (tid && !options.includes(tid)) { options.push(tid); labels[tid] = tid; }

  return (
    <div className="flex flex-col gap-4">
      <Section title="Runs" right={<div className="flex gap-2 items-center flex-wrap">
        <Select value={tid} onChange={setTid} options={options} labels={labels} />
        <Btn small onClick={res.reload}>Refresh</Btn>
      </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          One row per evaluation. A dry run only reads; a manual run is a “Send to these N now”; an automatic run is
          the scheduler (or Run now). Left out and capped list why people did not get it.
        </p>
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={runRows} csvName={`memac_runs${tid ? `_${tid}` : ''}`} defaultSort="-started_at"
            columns={['started_at', 'trigger_id', 'kind', 'actor', 'detected', 'eligible', 'sent', 'failed',
              'excluded_text', 'capped_text', 'send_id', 'error']}
            optionalColumns={['finished_at', 'id']}
            labels={{ started_at: 'When (IST)', trigger_id: 'Trigger', excluded_text: 'Left out', capped_text: 'Capped', send_id: 'Campaign' }}
            empty="No runs yet — nothing has been dry-run, sent or run for this."
            render={{
              started_at: r => fmtIST(r.started_at),
              finished_at: r => fmtIST(r.finished_at),
              trigger_id: r => r.trigger_id
                ? <button type="button" className="font-mono text-xs text-primary-700 hover:underline" onClick={() => onOpenTrigger(String(r.trigger_id))}>{r.trigger_id}</button>
                : '—',
              kind: r => <Badge tone={KIND_TONE[r.kind] || 'grey'}>{String(r.kind || '—').replace('_', ' ')}</Badge>,
              send_id: r => r.send_id
                ? <AdminLink to={{ group: 'email', tab: 'campaigns', campaign: String(r.send_id) }}>{String(r.send_id).slice(0, 10)}</AdminLink>
                : '—',
              error: r => r.error ? <span className="text-red-700" title={String(r.error)}>{String(r.error)}</span> : '—',
            }} />
        )}
        {(pages > 1 || page > 0) && (
          <div className="flex items-center gap-2 text-xs text-grayscale-1100">
            <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Newer</Btn>
            <span>Page {page + 1} of {pages}{total !== null ? ` · ${num(total)} runs` : ''}</span>
            <Btn small onClick={() => setPage(p => p + 1)} disabled={page + 1 >= pages}>Older</Btn>
          </div>
        )}
      </Section>

      {tid && (
        <Section title={`E-mails recorded for ${tid}${sendsTotal !== null ? ` (${num(sendsTotal)})` : ''}`}>
          <p className="text-xs text-grayscale-1100 -mt-1">
            The ledger rows this trigger wrote — what its caps count. An automatic mail is claimed (queued) before it
            is sent, so a failed one stays failed and is not retried.
          </p>
          {/* memac.runs: link_clicks {target: n} (its tracked buttons) and, for TL-02, answers {answer: people}. */}
          {[['Button clicks', d.link_clicks], ['One-click answers', d.answers]].map(([label, o]: any) => {
            const parts = o && typeof o === 'object' ? Object.entries(o).filter(([, v]) => Number(v) > 0) : [];
            return parts.length ? (
              <div key={label} className="text-xs text-grayscale-1200">
                <b>{label}:</b> {parts.map(([k, v]) => `${k.replace(/_/g, ' ')} ${num(v)}`).join(' · ')}
              </div>
            ) : null;
          })}
          <DataTable rows={ledger} csvName={`memac_ledger_${tid}`} defaultSort="-created_at"
            columns={['created_at', 'email', 'status', 'mode', 'occurrence_key', 'send_id', 'sent_by', 'error']}
            optionalColumns={['name', 'subject', 'user_id', 'template_key', 'category', 'primary_cta_label', 'updated_at']}
            labels={{ created_at: 'When (IST)', occurrence_key: 'Occurrence', send_id: 'Campaign' }}
            empty="Nothing sent for this trigger yet."
            render={{
              created_at: r => fmtIST(r.created_at),
              updated_at: r => fmtIST(r.updated_at),
              status: r => <Badge tone={STATUS_TONE[r.status] || 'grey'}>{String(r.status || '—')}</Badge>,
              email: r => r.email
                ? <AdminLink to={{ group: 'email', tab: 'people', email: String(r.email) }}>{r.email}</AdminLink> : '—',
              send_id: r => r.send_id
                ? <AdminLink to={{ group: 'email', tab: 'campaigns', campaign: String(r.send_id) }}>{String(r.send_id).slice(0, 10)}</AdminLink>
                : <span className="text-grayscale-1100">auto</span>,
            }} />
        </Section>
      )}
    </div>
  );
};

export default Runs;
